import React, { useCallback, useEffect, useRef, useState } from "react";
import { getSocialWallPosts } from "@/services/program/getSocialWallPosts";
import { connect } from "react-redux";
import { deleteSocialWallPost, setSocialWallPostType } from "@/redux/actions/socialWallPostActions";
import { useDispatch } from "react-redux";
import { likeSocialWallPost, pinSocialWallPost } from "@/redux/actions/socialWallPostActions";
import { useTranslation } from "react-i18next";
import { Themed } from '@/theme';
import {
  useDispatch as useFlashDispatch,
  flashError,
  flashSuccess,
} from "@/shared/components/flash";

const PAGE_SIZE = 10;

const isPinnedPost = (post) => {
  const value = post?.pin;
  return value === true || value === 1 || value === "1" || value === "true";
};

const mergeUniquePosts = (...groups) => {
  const byId = new Map();

  groups.flat().forEach((post) => {
    if (!post || typeof post.id === "undefined" || post.id === null) {
      return;
    }

    if (!byId.has(post.id)) {
      byId.set(post.id, post);
      return;
    }

    byId.set(post.id, {
      ...byId.get(post.id),
      ...post,
    });
  });

  return Array.from(byId.values());
};

const SocialWallPanel = ({
  organization,
  program,
  isManager,
  template,
  setPostType,
  emptyStateMessage = null,
  onStateChange = null,
}) => {
  const { t } = useTranslation();

  const [posts, setPosts]           = useState([]);
  const [pinnedPosts, setPinnedPosts] = useState([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [page, setPage]             = useState(0);
  const [hasMore, setHasMore]       = useState(false);
  const [loading, setLoading]       = useState(false);
  const [ready, setReady]           = useState(false);

  const [socialWallPost, setSocialWallPost] = useState(null);
  const [isOpen, setOpen]                  = useState(false);

  const confirmRef  = useRef({});
  const loadingRef  = useRef(false);   // guard against concurrent fetches
  const observerRef = useRef(null);

  const dispatch      = useDispatch();
  const flashDispatch = useFlashDispatch();
  const socialWallPostFilter = { isManager };
  const mergedPosts = mergeUniquePosts(pinnedPosts, posts);

  const syncPinnedPosts = useCallback((pageData) => {
    if (!organization?.id || !program?.id) {
      setPinnedPosts((pageData?.results || []).filter(isPinnedPost));
      return Promise.resolve();
    }

    const visiblePinnedPosts = (pageData?.results || []).filter(isPinnedPost);

    if (!isManager) {
      setPinnedPosts(visiblePinnedPosts);
      return Promise.resolve();
    }

    if (!pageData?.count || pageData.count <= PAGE_SIZE) {
      setPinnedPosts(visiblePinnedPosts);
      return Promise.resolve();
    }

    return getSocialWallPosts(
      organization.id,
      program.id,
      0,
      pageData.count,
      socialWallPostFilter
    )
      .then((allPostsData) => {
        setPinnedPosts((allPostsData?.results || []).filter(isPinnedPost));
      })
      .catch((error) => {
        console.error(error);
        setPinnedPosts((previous) => mergeUniquePosts(previous, visiblePinnedPosts));
      });
  }, [organization?.id, program?.id, isManager]);

  // ── Core fetch ───────────────────────────────────────────────────────────────

  const fetchPage = useCallback((pageNum, append) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    getSocialWallPosts(organization.id, program.id, pageNum, PAGE_SIZE, socialWallPostFilter)
      .then((data) => {
        setPosts(prev => append ? mergeUniquePosts(prev, data.results) : data.results);
        setTotalPosts(data.count);
        setHasMore((pageNum + 1) * PAGE_SIZE < data.count);
        setReady(true);

        if (pageNum === 0) {
          syncPinnedPosts(data);
        }
      })
      .catch((error) => {
        console.error(error);
        setPosts([]);
        setPinnedPosts([]);
        setTotalPosts(0);
        setHasMore(false);
        setReady(true);
      })
      .finally(() => {
        setLoading(false);
        loadingRef.current = false;
      });
  }, [organization?.id, program?.id, isManager, syncPinnedPosts]);

  // ── Initial load ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (organization && program) {
      setPage(0);
      setPinnedPosts([]);
      fetchPage(0, false);
    }
  }, [organization?.id, program?.id]);

  useEffect(() => {
    if (typeof onStateChange === "function") {
      onStateChange({
        ready,
        hasPosts: mergedPosts.length > 0,
      });
    }
  }, [ready, mergedPosts.length, onStateChange]);

  // ── IntersectionObserver — callback ref so observer is tied to element lifecycle

  const sentinelRef = useCallback((node) => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!node || !hasMore) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingRef.current) {
          setPage(prev => {
            const next = prev + 1;
            fetchPage(next, true);
            return next;
          });
        }
      },
      { rootMargin: '300px' }
    );

    observerRef.current.observe(node);
  }, [hasMore, fetchPage]);

  // ── Refresh helper (used after like / pin / delete) ──────────────────────────

  const refreshFeed = useCallback(() => {
    setPage(0);
    fetchPage(0, false);
  }, [fetchPage]);

  // ── setSocialWallPosts — called by child components after comment submit ─────
  // Accepts the data object from the child's own fetch and resets the feed.

  const setSocialWallPosts = useCallback((data) => {
    setPosts(data.results);
    setTotalPosts(data.count);
    setHasMore(PAGE_SIZE < data.count);
    setPage(0); // next scroll load will fetch page 1
    syncPinnedPosts(data);
  }, [syncPinnedPosts]);

  // ── Event handlers ───────────────────────────────────────────────────────────

  const popupToggle  = () => setOpen(prev => !prev);
  const onclickAddPost = () => { setPostType("message"); setOpen(true); };

  const deleteActivity = (id) => {
    dispatch(deleteSocialWallPost(organization.id, program.id, id))
      .then(() => {
        setPinnedPosts((previous) => previous.filter((post) => post.id !== id));
        refreshFeed();
        confirmRef.current.toggle();
        flashSuccess(flashDispatch, "Deleted successfully.");
      })
      .catch((err) => flashError(flashDispatch, err));
  };

  const LikeActivityEvent = (id) => {
    dispatch(likeSocialWallPost(organization.id, program.id, { id }))
      .then(refreshFeed)
      .catch(console.error);
  };

  const PinActivityEvent = (id) => {
    dispatch(pinSocialWallPost(organization.id, program.id, { id }))
      .then(() => {
        const currentPost = mergedPosts.find((post) => post.id === id);

        if (!currentPost) {
          refreshFeed();
          return;
        }

        const toggledPost = {
          ...currentPost,
          pin: isPinnedPost(currentPost) ? 0 : 1,
        };

        setPosts((previous) => previous.map((post) => (
          post.id === id ? toggledPost : post
        )));

        setPinnedPosts((previous) => {
          if (isPinnedPost(toggledPost)) {
            return mergeUniquePosts([toggledPost], previous);
          }

          return previous.filter((post) => post.id !== id);
        });
      })
      .catch(console.error);
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  const socialEmptyStateClassName = `hd__activity-empty hd__activity-empty--social${isManager ? " hd__activity-empty--social-manager" : ""}`;

  if (!ready) {
    return emptyStateMessage
      ? <div className={socialEmptyStateClassName}>{t("loading")}</div>
      : t("loading");
  }

  if (emptyStateMessage && mergedPosts.length === 0) {
    if (typeof onStateChange === "function") {
      return null;
    }

    return <div className={socialEmptyStateClassName}>{emptyStateMessage}</div>;
  }

  const socialWallPosts = {
    results: mergedPosts,
    count: totalPosts,
  };

  const themedProps = {
    isManager,
    socialWallPosts,
    template,
    program,
    popupToggle,
    confirmRef,
    setSocialWallPost,
    LikeActivityEvent,
    PinActivityEvent,
    setOpen,
    socialWallPost,
    deleteActivity,
    setSocialWallPosts,
    isOpen,
    onclickAddPost,
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  };

  return (
    <>
      <Themed component="SocialWall" {...themedProps} />

      {hasMore && <div ref={sentinelRef} className="sw-load-sentinel" />}

      {loading && (
        <div className="sw-loading-more">
          <span className="sw-loading-spinner" />
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  program:      state.program,
  organization: state.organization,
  template:     state.template,
});

const mapDispatchToProps = dispatch => ({
  setPostType: (type) => dispatch(setSocialWallPostType(type)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SocialWallPanel);
